// Complaints — support-ticket queue. Independent table from disputes; rows
// can be anonymous (no `fromUserId`, only `fromEmail`). Default sort is by
// priority then opened_at so urgent items rise to the top automatically.

import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import { PageHeader, ToolChip, ToolSearch, Toolbar } from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import {
  fetchAdminComplaints,
  type AdminComplaintRow,
  type AdminComplaintsListParams,
  type ComplaintStatus,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type StatusFilter = ComplaintStatus | 'open_only' | 'overdue' | 'all';

const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: 'open_only', label: 'Open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'breached', label: 'Breached' },
  { key: 'closed', label: 'Closed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const hour = 3_600_000;
  const day = 24 * hour;
  if (abs < hour) {
    const m = Math.round(abs / 60_000);
    return diffMs >= 0 ? `in ${m}m` : `${m}m ago`;
  }
  if (abs < day) {
    const h = Math.round(abs / hour);
    return diffMs >= 0 ? `in ${h}h` : `${h}h ago`;
  }
  const days = Math.round(abs / day);
  return diffMs >= 0 ? `in ${days}d` : `${days}d ago`;
}

const columns: DataTableColumn<AdminComplaintRow>[] = [
  {
    key: 'priority',
    h: 'Priority',
    cell: (r) => <StatusPill status={r.priority} />,
  },
  {
    key: 'subject',
    h: 'Complaint',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.subject}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.kind} · {r.channel}
        </div>
      </div>
    ),
  },
  {
    key: 'from',
    h: 'From',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate">{r.fromName ?? r.fromEmail ?? 'Anonymous'}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.fromEmail ?? '—'}
        </div>
      </div>
    ),
  },
  {
    key: 'status',
    h: 'Status',
    cell: (r) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusPill status={r.status} />
        {r.overdue && r.status !== 'breached' ? <StatusPill status="breached" /> : null}
      </div>
    ),
  },
  {
    key: 'assignee',
    h: 'Assigned',
    cell: (r) =>
      r.assigneeEmail ? (
        <span className="truncate text-[12px]">{r.assigneeEmail}</span>
      ) : (
        <span className="text-[11.5px] text-[color:var(--bo-mute)]">Unassigned</span>
      ),
  },
  {
    key: 'sla',
    h: 'SLA',
    cell: (r) => (
      <span
        className={`tabular-nums text-[12px] ${r.overdue ? 'font-semibold text-[#9f1239]' : 'text-[color:var(--bo-mute)]'}`}
      >
        {r.slaDueAt ? formatRelative(r.slaDueAt) : '—'}
      </span>
    ),
  },
  {
    key: 'opened',
    h: 'Opened',
    cell: (r) => <span className="tabular-nums text-[12px]">{formatDate(r.openedAt)}</span>,
  },
];

export function Complaints() {
  const [status, setStatus] = useState<StatusFilter>('open_only');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();
  const navigate = useNavigate();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [status, q, setPage]);

  const params: AdminComplaintsListParams = {
    status,
    q: q.trim() || undefined,
    page,
    limit,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'complaints', params],
    queryFn: ({ signal }) => fetchAdminComplaints(params, signal),
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Trust"
        title="Complaints"
        sub={`${total.toLocaleString()} complaints${status === 'all' ? '' : ` · ${status.replace(/_/g, ' ')}`} · Support tickets with SLA timers + assignment.`}
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load complaints: {(error as Error).message}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search subject or email…" />
        {STATUS_FILTERS.map((f) => (
          <ToolChip key={f.key} active={status === f.key} onClick={() => setStatus(f.key)}>
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      <DataTable
        columns={columns}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No complaints match these filters.'}
        onRowClick={(r) => void navigate({ to: `/complaints/${r.id}` as never })}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
    </div>
  );
}
